import { useState, useEffect, useMemo } from "react";
import { AlertTriangle, RefreshCw, ChevronRight } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * Bannière d'alerte pour les R1 urgents à confirmer
 * Affiche les clients dont le R1 approche et qui n'ont pas encore confirmé
 * - Si 1 seul client : affichage direct
 * - Si plusieurs clients : système repliable (fermé par défaut)
 */
export function UrgentR1Banner({ clients, onClientUpdate, defaultExpanded = false }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [updatingRow, setUpdatingRow] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleConfirmationChange = async (client, value) => {
    setUpdatingRow(client.rowNumber);
    try {
      const response = await authFetch(`${API}/api/telepro/update-rdv-confirme?row_number=${client.rowNumber}&value=${value}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        if (onClientUpdate) {
          onClientUpdate();
        }
      }
    } catch (error) {
      console.error('Error updating RDV confirmation:', error);
    } finally {
      setUpdatingRow(null);
    }
  };

  const urgentR1s = useMemo(() => {
    if (!clients) return [];
    
    const now = currentTime;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const isBeforeDeadline = currentHour < 10 || (currentHour === 10 && currentMinutes <= 30);
    const todayDayOfWeek = today.getDay();
    
    const urgent = [];
    
    clients.forEach(client => {
      if (client.rdvConfirme?.toLowerCase() === 'oui') return;
      
      const dateR1 = client.dateR1;
      if (!dateR1) return;
      
      const parts = dateR1.split('/');
      if (parts.length !== 3) return;
      
      try {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const r1Date = new Date(year, month - 1, day);
        const r1DayOfWeek = r1Date.getDay();
        
        const diffTime = r1Date.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        let isUrgent = false;
        let urgencyReason = '';
        
        if (diffDays === 1) {
          isUrgent = true;
          urgencyReason = 'À confirmer aujourd\'hui';
        }
        
        if (diffDays === 0 && isBeforeDeadline) {
          isUrgent = true;
          urgencyReason = 'À confirmer avant 10h30';
        }
        
        if (todayDayOfWeek === 5 && r1DayOfWeek === 1 && diffDays === 3) {
          isUrgent = true;
          urgencyReason = 'R1 lundi - À confirmer avant le week-end';
        }
        
        if (todayDayOfWeek === 6 && r1DayOfWeek === 1 && diffDays === 2) {
          isUrgent = true;
          urgencyReason = 'R1 lundi';
        }
        
        if (isUrgent) {
          urgent.push({
            ...client,
            r1Date: dateR1,
            r1DateObj: r1Date,
            urgencyReason,
            diffDays
          });
        }
      } catch (e) {
        // Ignorer les erreurs de parsing
      }
    });
    
    return urgent.sort((a, b) => a.r1DateObj - b.r1DateObj);
  }, [clients, currentTime]);

  if (urgentR1s.length === 0) return null;

  const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const hasMultiple = urgentR1s.length > 1;
  const showList = !hasMultiple || expanded;

  const renderClientItem = (client, idx) => (
    <div 
      key={idx}
      data-testid={`urgent-r1-client-${idx}`}
      className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between gap-4"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${client.diffDays === 0 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`} />
        <div className="min-w-0">
          <p className="text-white font-medium truncate">
            {client.nom} {client.prenom}
            {client.telephone ? (
              <span className="ml-2 text-blue-400 font-normal">📞 {client.telephone}</span>
            ) : (
              <span className="ml-2 text-red-400 text-xs font-normal">⚠️ Tél. manquant</span>
            )}
          </p>
          <p className="text-xs text-slate-400 truncate">
            Télépro: {client.telepro || 'Non assigné'}
          </p>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-white font-medium text-sm">
          R1 : {client.r1Date} ({dayNames[client.r1DateObj.getDay()]})
        </p>
        <p className={`text-xs ${client.diffDays === 0 ? 'text-red-400 font-bold' : 'text-orange-400'}`}>
          {client.urgencyReason}
        </p>
      </div>
      <div className="flex-shrink-0">
        {updatingRow === client.rowNumber ? (
          <div className="w-24 h-9 bg-slate-700 rounded-lg flex items-center justify-center">
            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
          </div>
        ) : (
          <select
            data-testid={`confirm-select-${idx}`}
            value={client.rdvConfirme?.toUpperCase() || ''}
            onChange={(e) => handleConfirmationChange(client, e.target.value)}
            className={`w-24 px-3 py-2 rounded-lg text-sm font-bold cursor-pointer transition-colors ${
              client.rdvConfirme?.toLowerCase() === 'oui' 
                ? 'bg-green-600 text-white border-green-500' 
                : client.rdvConfirme?.toLowerCase() === 'non'
                  ? 'bg-red-600 text-white border-red-500'
                  : 'bg-slate-700 text-slate-300 border-slate-600'
            } border`}
          >
            <option value="" className="bg-slate-800 text-slate-300">-</option>
            <option value="OUI" className="bg-green-600 text-white">OUI</option>
            <option value="NON" className="bg-red-600 text-white">NON</option>
          </select>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="urgent-r1-banner" className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border border-red-500/50 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div 
            className={`flex items-center justify-between ${hasMultiple ? 'cursor-pointer' : ''}`}
            onClick={() => hasMultiple && setExpanded(!expanded)}
          >
            <div>
              <h3 className="font-bold text-red-300 flex items-center gap-2">
                🚨 Urgent - Confirmez vos R1 !
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                  {urgentR1s.length} à confirmer
                </span>
              </h3>
              <p className="text-xs text-red-200/70 mt-0.5">
                Deadline : la veille du R1 ou le jour même avant 10h30
              </p>
            </div>
            {hasMultiple && (
              <ChevronRight className={`w-5 h-5 text-red-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            )}
          </div>
          
          {showList && (
            <div className="space-y-2 mt-3 max-h-[250px] overflow-auto">
              {urgentR1s.map((client, idx) => renderClientItem(client, idx))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UrgentR1Banner;
